<script setup>
import { computed } from 'vue';
import Button from 'dashboard/components-next/button/Button.vue';

const props = defineProps({
  agent: {
    type: Object,
    required: true,
  },
  isLoading: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(['click', 'sync', 'delete', 'playground']);

const isSynced = computed(() => !!props.agent.external_agent_id);

const truncateText = (text, maxLength = 100) => {
  if (!text) return '';
  return text.length > maxLength ? `${text.substring(0, maxLength)}...` : text;
};
</script>

<template>
  <div
    class="group relative bg-n-solid-1 border border-n-weak rounded-2xl p-5 hover:border-n-brand hover:shadow-lg transition-all duration-200 cursor-pointer"
    @click="emit('click', agent)"
  >
    <!-- Status Badge -->
    <div class="absolute top-4 right-4 flex items-center gap-2">
      <span
        class="px-2.5 py-1 text-xs font-medium rounded-full"
        :class="
          isSynced
            ? 'bg-green-500/10 text-green-500'
            : 'bg-yellow-500/10 text-yellow-500'
        "
      >
        {{ isSynced ? 'Sincronizado' : 'Nao sincronizado' }}
      </span>
    </div>

    <!-- Agent Icon & Name -->
    <div class="flex items-start gap-4 mb-4">
      <div
        class="w-14 h-14 rounded-xl bg-gradient-to-br from-n-brand/20 to-n-brand/5 flex items-center justify-center flex-shrink-0"
      >
        <span class="i-lucide-bot text-2xl text-n-brand" />
      </div>
      <div class="flex-1 min-w-0 pt-1">
        <h3 class="text-lg font-semibold text-n-slate-12 truncate">
          {{ agent.name }}
        </h3>
        <span v-if="agent.model" class="text-xs text-n-slate-10 mt-1">
          {{ agent.model }}
        </span>
      </div>
    </div>

    <!-- Description -->
    <p
      v-if="agent.description"
      class="text-sm text-n-slate-11 mb-4 line-clamp-2"
    >
      {{ truncateText(agent.description, 120) }}
    </p>

    <!-- Role -->
    <div v-if="agent.role" class="mb-4">
      <span class="text-xs text-n-slate-10 uppercase tracking-wide">Role</span>
      <p class="text-sm text-n-slate-12 mt-1 truncate">
        {{ agent.role }}
      </p>
    </div>

    <!-- Actions -->
    <div
      class="flex items-center justify-between pt-4 border-t border-n-weak mt-auto"
    >
      <div class="flex items-center gap-1">
        <Button
          v-tooltip="'Testar no Playground'"
          icon="i-lucide-play"
          variant="ghost"
          size="sm"
          :disabled="!isSynced"
          @click.stop="emit('playground', agent)"
        />
        <Button
          v-tooltip="'Sincronizar'"
          icon="i-lucide-refresh-cw"
          variant="ghost"
          size="sm"
          :is-loading="isLoading"
          @click.stop="emit('sync', agent)"
        />
      </div>
      <Button
        v-tooltip="'Excluir'"
        icon="i-lucide-trash-2"
        variant="ghost"
        size="sm"
        color="ruby"
        @click.stop="emit('delete', agent)"
      />
    </div>

    <!-- Hover indicator -->
    <div
      class="absolute inset-0 rounded-2xl border-2 border-transparent group-hover:border-n-brand/30 pointer-events-none transition-colors"
    />
  </div>
</template>
